"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.newJFDImagesDef = exports.newJFDDef = void 0;
const json_schema_faker_1 = __importDefault(require("json-schema-faker"));
const types_1 = require("./types");
function newJFDDef(def, rootJFDDef, parentFieldName) {
    const fields = {};
    Object.entries(def.fields || {}).forEach(([fieldName, field]) => {
        fields[fieldName] = newJFDFieldDef(fieldName, field, rootJFDDef || def, parentFieldName);
    });
    const images = newJFDImagesDef(def.images || {});
    return {
        bookingNumberFieldName: def.bookingNumberFieldName || '',
        masterKeyFieldName: def.masterKeyFieldName || '',
        convertToUpperCase: def.convertToUpperCase || false,
        images,
        fields,
        rules: def.rules || [],
        onInit: def.onInit || [],
        onCreate: def.onCreate || [],
        onGenerateEBTSFile: def.onGenerateEBTSFile || [],
        onImport: def.onImport || [],
        imageRules: def.imageRules || [],
        onLoadCaptureStationInfo: def.onLoadCaptureStationInfo || []
    };
}
exports.newJFDDef = newJFDDef;
function newJFDFieldDef(fieldName, def, rootJFDDef, parentFieldName) {
    let subFields;
    if (def.subFields) {
        subFields = newJFDDef(def.subFields, rootJFDDef, fieldName);
    }
    else {
        subFields = {
            // we include `bookingNumberFieldName`, `images`, `imageRules` and `onInit`
            // here only to appease the recursive nature of JFDDef, it doesn't make
            // sense to define those in a subfield
            // ToDo remove these from the subfields definition
            bookingNumberFieldName: '',
            masterKeyFieldName: '',
            convertToUpperCase: false,
            images: { modalities: {}, bioTypes: {} },
            imageRules: [],
            fields: {},
            rules: [],
            onInit: [],
            onCreate: [],
            onGenerateEBTSFile: [],
            onImport: [],
            onLoadCaptureStationInfo: []
        };
    }
    // Min/max occurences
    let minOccurrences;
    if (def.minOccurrences === undefined) {
        minOccurrences = 1;
    }
    else {
        minOccurrences = def.minOccurrences;
    }
    // Check that if maxOccurrences is defined, it isn't less than minOccurrences
    let maxOccurrences;
    if (def.maxOccurrences === undefined) {
        // Max occurrences must be at least minOccurrences to avoid out of bounds
        maxOccurrences = Math.max(1, minOccurrences);
    }
    else {
        if (def.maxOccurrences < minOccurrences)
            throw new Error(`Invalid JFD definition, field=${fieldName} minOccurrences=${minOccurrences} maxOccurrences=${def.maxOccurrences}`);
        else {
            maxOccurrences = def.maxOccurrences;
        }
    }
    let convertToUpperCase;
    if (def.convertToUpperCase === undefined) {
        if (rootJFDDef.convertToUpperCase === undefined) {
            convertToUpperCase = false;
        }
        else {
            convertToUpperCase = rootJFDDef.convertToUpperCase;
        }
    }
    else {
        convertToUpperCase = def.convertToUpperCase;
    }
    // compute full name
    // note: this sets the fullName only for JFDFieldDefs and parent JFDFields,
    // not JFDFields that are subfields; there's some extra code in JFD.makeSField()
    // that does it.
    let fullName;
    if (parentFieldName) {
        fullName = `${parentFieldName}.${fieldName}`;
    }
    else {
        fullName = `${fieldName}`;
    }
    // This logic prevents the 5 digit years on the date fields when the min field value is not defined on the JFD.
    const defaultMaxDate = new Date();
    defaultMaxDate.setFullYear(defaultMaxDate.getFullYear() + 50);
    const defaultMinDate = new Date();
    defaultMinDate.setFullYear(defaultMinDate.getFullYear() - 150);
    return {
        name: fieldName,
        fullName,
        type: def.type || 'string',
        label: def.label || fieldName,
        defaultValue: def.defaultValue || (def.defaultFunction ? def.defaultFunction() : ''),
        desc: def.desc || '',
        required: def.required === undefined ? true : def.required,
        convertToUpperCase,
        minLength: def.minLength || 0,
        maxLength: def.maxLength || 0,
        minOccurrences,
        maxOccurrences,
        regex: def.regex,
        readOnly: def.readOnly || false,
        options: def.options || [],
        allOptions: def.allOptions || {},
        subFields,
        rules: def.rules || [],
        placeholder: def.placeholder || '',
        mock: def.mock,
        EBTS: def.EBTS,
        minDate: def.minDate || defaultMinDate.toISOString().split('T')[0],
        maxDate: def.maxDate || defaultMaxDate.toISOString().split('T')[0],
        hasSubFields() {
            return Object.keys(this.subFields.fields).length > 0;
        },
        hasMultipleOccurrences() {
            return this.minOccurrences > 1 || this.maxOccurrences > 1;
        },
        getType() {
            if (this.hasMultipleOccurrences()) {
                if (this.hasSubFields()) {
                    return 'MOS';
                }
                else {
                    return 'MONS';
                }
            }
            else {
                if (this.hasSubFields()) {
                    return 'SOS';
                }
                else {
                    return 'SONS';
                }
            }
        },
        /**
         * Definition of mock function that will return mocked data
         */
        getMockData() {
            var _a, _b;
            // If the field has options and is not [], select one and ignore mock
            if (this.options && this.options.length > 0) {
                // Return a random choice from the options
                // We use a bitwise OR (|) to get rid of the float part
                const [key] = this.options[(Math.random() * this.options.length) | 0];
                return `${key}`;
            }
            let format = this.mock;
            // If func mock starts with value: use all that follows as the value
            if (format === null || format === void 0 ? void 0 : format.startsWith('value:'))
                return format.replace('value:', '');
            // We need to ignore the lenght constraints if they are === 0
            const minLength = this.minLength || undefined;
            const maxLength = this.maxLength || undefined;
            // If there's a format, try using that
            if (format) {
                const value = json_schema_faker_1.default.generate({ type: 'string', format, minLength, maxLength });
                // Validate against the regex
                if (!((_a = this.regex) === null || _a === void 0 ? void 0 : _a.test(value)))
                    throw new types_1.InvalidFormatError(format);
                return value;
            }
            // Here we try to generate data based on the field type, as well as EBTS's dataType, format and specialChars
            // fields
            // If type is date, there's really no other way around. Use EBTS's format part as well
            if (this.type === 'date') {
                // First we need to get rid of that Century (CC) part. We also need some way of translating the format string.
                // Using the (ISO-8601) yyyy-mm-dd format is good for now I think.
                // TODO: Account for the 27-character version i.e. ±YYYYYY-MM-DD
                return json_schema_faker_1.default.generate({ type: 'string', format: 'date-time' }).split('T')[0];
            }
            else {
                if (((_b = this.EBTS) === null || _b === void 0 ? void 0 : _b.dataType) === 'N') {
                    // For random numbers we have to take into account the min and max number of characters
                    // But if the field has a pattern, just use that
                    if (this.regex)
                        return json_schema_faker_1.default.generate({ type: 'string', pattern: this.regex.source });
                    // Get a value between the minimum and maximum possible n-digit numbers, 0 excluded unless length is 0 or
                    // undefined
                    const min = this.minLength ? Math.pow(10, this.minLength - 1) : 0;
                    // Limit the max number to just below the next power of 10 from the min
                    const max = this.maxLength ? Math.pow(10, this.maxLength) - 1 : min * 10 - 1;
                    return json_schema_faker_1.default.generate({ type: 'integer', minimum: min, maximum: max }).toString();
                }
                // Other types (A, AN, ANS) have either options or can be generated by the default function, afaik
            }
            // Generate a value with length constraints
            let mock = json_schema_faker_1.default.generate({ type: 'string', minLength, maxLength });
            // Ensure it doesn't end in ' '
            mock = mock.trim();
            const regex = this.regex;
            // And validate against the regex
            if (regex) {
                // Return it if it passes
                if (regex.test(mock))
                    return mock;
                // Otherwise just use it to generate a value
                return json_schema_faker_1.default.generate({ type: 'string', pattern: regex.source, minLength, maxLength });
            }
            // If there is no regex available return the mocked value
            return mock;
        },
        // note: we don't use a getter here since we use the object spread operator
        // when creating a JFDField from a JFDFieldDef, and that operator doesn't
        // includes getters, see https://stackoverflow.com/a/56097985/9778302
        isApplicable() {
            return this.required !== null;
        }
    };
}
function newJFDImagesDef(def) {
    const modalities = {};
    Object.entries(def.modalities || {}).forEach(([modality, modalityDef]) => {
        modalities[modality] = {
            minOccurrences: (modalityDef === null || modalityDef === void 0 ? void 0 : modalityDef.minOccurrences) || 0,
            maxOccurrences: (modalityDef === null || modalityDef === void 0 ? void 0 : modalityDef.maxOccurrences) || 0,
            type: modalityDef === null || modalityDef === void 0 ? void 0 : modalityDef.type
        };
    });
    const bioTypes = {};
    Object.entries(def.bioTypes || {}).forEach(([bioType, bioTypeDef]) => {
        bioTypes[bioType] = {
            required: (bioTypeDef === null || bioTypeDef === void 0 ? void 0 : bioTypeDef.required) || false
        };
    });
    return {
        modalities,
        bioTypes
    };
}
exports.newJFDImagesDef = newJFDImagesDef;
//# sourceMappingURL=jfd-def.js.map